import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label
} from 'reactstrap';
import { Formik, Field } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { UPDATE_DOCTOR_MUTATION } from '../queries';
import { MdRateReview } from 'react-icons/md';

const validationSchema = Yup.object().shape({
  reviewLink: Yup.string()
    .trim()
    .required('Please enter a review link')
    .matches(
      /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}([/?].*)?$/,
      'Please enter a valid https:// URL with a proper domain'
    )
    .url('Please enter a valid URL')
});

const DoctorReviewLinkModal = ({ doctor, hospitalId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(prev => !prev);

  const [updateDoctor, { loading }] = useMutation(UPDATE_DOCTOR_MUTATION);

  const initialLink = useMemo(() => {
    if (!doctor) return '';
    const linksObj = doctor.unitsWiseReviewLinks || {};
    return linksObj[hospitalId]
      ? linksObj[hospitalId]
      : doctor && doctor.doctorReviewLink
      ? doctor.doctorReviewLink
      : '';
  }, [doctor, hospitalId]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const currentObj = doctor.unitsWiseReviewLinks || {};
      const updatedObj = {
        ...currentObj,
        [hospitalId]: values.reviewLink
      };

      await updateDoctor({
        variables: {
          docInput: {
            id: doctor.id,
            unitsWiseReviewLinks: updatedObj
          }
        }
      });

      toast.success('Review link saved successfully', {
        autoClose: 3000,
        onClose: () => {
          window.location.reload();
        }
      });

      toggle();
    } catch {
      toast.error('Failed to save review link', { autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!doctor) return null;

  return (
    <>
      <Button
        color="link"
        size="sm"
        onClick={toggle}
        style={{ padding: 0, margin: 0 }}
        title="Doctor Review Link"
      >
        <MdRateReview color="#007bff" size="1.2em" />
      </Button>

      <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          Doctor Review Link — {doctor.name}
        </ModalHeader>

        <Formik
          key={initialLink}
          initialValues={{ reviewLink: initialLink }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, handleSubmit, isSubmitting }) => (
            <>
              <ModalBody>
                <FormGroup>
                  <Label for="reviewLink">Review Link *</Label>
                  <Field
                    name="reviewLink"
                    type="url"
                    className={`form-control ${
                      touched.reviewLink && errors.reviewLink
                        ? 'is-invalid'
                        : ''
                    }`}
                    placeholder="https://www.app.com/review"
                  />
                  {touched.reviewLink && errors.reviewLink && (
                    <div className="invalid-feedback d-block">
                      {errors.reviewLink}
                    </div>
                  )}
                  <small className="text-muted">
                    This link is specific to selected hospital
                  </small>
                </FormGroup>
              </ModalBody>

              <ModalFooter>
                <Button
                  color="secondary"
                  onClick={toggle}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || loading}
                >
                  {loading || isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>
    </>
  );
};

DoctorReviewLinkModal.propTypes = {
  doctor: PropTypes.object.isRequired,
  hospitalId: PropTypes.number.isRequired
};

export default DoctorReviewLinkModal;
